import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { Menu, Phone, CalendarCheck, X, ChevronDown } from "lucide-react";
import { BUSINESS, NAV } from "../../content/site";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

// Lightweight accessible drawer (no UI library): focus moves in, Esc/overlay closes, focus returns, page scroll locked.
export const MobileMenu = () => {
  const [open, setOpen] = useState(false);
  const btn = useRef(null);
  const panel = useRef(null);
  const { pathname } = useLocation();
  const close = () => setOpen(false);

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    const trigger = btn.current;
    document.body.style.overflow = "hidden";
    panel.current?.querySelector("button, a")?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
      if (e.key === "Tab" && panel.current) {
        const f = [...panel.current.querySelectorAll("a, button, summary")];
        if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; document.removeEventListener("keydown", onKey); trigger?.focus(); };
  }, [open]);

  return (
    <>
      <button ref={btn} type="button" aria-label="Open menu" aria-expanded={open} aria-controls="mobile-menu" onClick={() => setOpen(true)} className="grid h-11 w-11 place-items-center rounded-full border border-gray-200 text-plum" data-testid="mobile-menu-open-btn">
        <Menu className="h-5 w-5" aria-hidden="true" />
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true" aria-labelledby="mobile-menu-title">
          <div className="absolute inset-0 bg-black/50" onClick={close} aria-hidden="true" />
          <div id="mobile-menu" ref={panel} className="absolute inset-y-0 right-0 flex w-full max-w-sm flex-col bg-white shadow-2xl" data-testid="mobile-menu-drawer">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <p id="mobile-menu-title" className="font-display text-lg font-bold text-plum">Menu</p>
              <button type="button" onClick={close} aria-label="Close menu" className="grid h-11 w-11 place-items-center rounded-full text-ink hover:bg-mist" data-testid="mobile-menu-close-btn"><X className="h-5 w-5" aria-hidden="true" /></button>
            </div>
            <nav aria-label="Mobile" className="flex-1 overflow-y-auto overscroll-contain px-5 py-2">
              {NAV.map((g) => (
                <details key={g.label} className="group border-b">
                  <summary className="flex cursor-pointer list-none items-center justify-between py-4 text-base font-semibold text-ink [&::-webkit-details-marker]:hidden" data-testid={`mobile-menu-group-${slug(g.label)}`}>
                    {g.label}
                    <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" aria-hidden="true" />
                  </summary>
                  <ul className="space-y-1 pb-3">
                    {g.columns.flatMap((c) => c.links).map(([label, href]) => (
                      <li key={href + label}>
                        <Link to={href} onClick={close} className="block rounded-lg px-3 py-2.5 text-[15px] text-ink-soft hover:bg-plum-soft hover:text-plum" data-testid={`mobile-menu-link-${slug(label)}`}>{label}</Link>
                      </li>
                    ))}
                  </ul>
                </details>
              ))}
              <Link to="/blog" onClick={close} className="block border-b py-4 text-base font-semibold text-ink" data-testid="mobile-menu-link-blog">Blog</Link>
              <Link to="/contact" onClick={close} className="block border-b py-4 text-base font-semibold text-ink" data-testid="mobile-menu-link-contact">Contact</Link>
              <Link to="/free-water-heater-replacement" onClick={close} className="block py-4 text-base font-semibold text-plum" data-testid="mobile-menu-link-water-heater-estimate">Free Water Heater Estimate</Link>
            </nav>
            <div className="grid grid-cols-2 gap-3 border-t bg-mist p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
              <a href={BUSINESS.phoneHref} className="btn-plum px-3" data-testid="mobile-menu-call-btn"><Phone className="h-4 w-4" aria-hidden="true" />Call Now</a>
              <Link to="/schedule-service" onClick={close} className="btn-gold px-3" data-testid="mobile-menu-schedule-btn"><CalendarCheck className="h-4 w-4" aria-hidden="true" />Schedule</Link>
            </div>
          </div>
        </div>
      , document.body)}
    </>
  );
};

export const MobileStickyBar = () => (
  <div className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-2 gap-2 border-t bg-white/95 px-2.5 pt-2.5 pb-[calc(0.625rem+env(safe-area-inset-bottom))] backdrop-blur lg:hidden" data-testid="mobile-sticky-cta">
    <a href={BUSINESS.phoneHref} className="btn-plum min-h-[48px] px-3 py-2.5 text-sm" data-testid="sticky-call-btn"><Phone className="h-4 w-4" aria-hidden="true" />{BUSINESS.phone}</a>
    <Link to="/schedule-service" className="btn-gold min-h-[48px] px-3 py-2.5 text-sm" data-testid="sticky-schedule-btn"><CalendarCheck className="h-4 w-4" aria-hidden="true" />Schedule<span className="-ml-1 hidden min-[380px]:inline">Service</span></Link>
  </div>
);
